import { gql } from '@apollo/client';

import { useOrgSettingsNavIntegrationsQuery } from 'src/generated/mloop-graphql';

export type IntegrationSettingsPageKey =
  | 'GREENHOUSE'
  | 'LEVER'
  | 'ASHBY'
  | 'WORKDAY'
  | 'MICROSOFT'
  | 'MICROSOFT_GCC_HIGH'
  | 'MICROSOFT_SERVICE_USER'
  | 'GOOGLE_WORKSPACE'
  | 'SMARTRECRUITERS'
  | 'GEM'
  | 'SLACK'
  | 'ZOOM'
  | 'CODERPAD'
  | 'CODILITY'
  | 'CODESIGNAL'
  | 'HACKERRANK';

/** Returns the connection predicates used to decide which integration settings links are visible. */
export const useConnectedIntegrations = () => {
  const { data, loading } = useOrgSettingsNavIntegrationsQuery();
  const integrations = data?.thisOrg?.integrations;
  return {
    loading,
    connected: {
      GREENHOUSE: Boolean(integrations?.greenhouseOauth),
      LEVER: Boolean(integrations?.lever?.active),
      ASHBY: Boolean(integrations?.ashby),
      WORKDAY: Boolean(integrations?.workday),
      // Presence (not `active`) mirrors the hub cards and the dedicated page's own gating.
      MICROSOFT: Boolean(integrations?.microsoft || integrations?.microsoftCustom),
      MICROSOFT_GCC_HIGH: Boolean(integrations?.microsoftGccHigh?.active),
      MICROSOFT_SERVICE_USER: Boolean(integrations?.microsoftServiceUser),
      GOOGLE_WORKSPACE: Boolean(integrations?.google),
      SMARTRECRUITERS: Boolean(integrations?.smartRecruiters?.active),
      GEM: Boolean(integrations?.gem),
      SLACK: Boolean(integrations?.slack?.active),
      ZOOM: Boolean(integrations?.zoom?.active || integrations?.zoomCustom?.active),
      CODERPAD: Boolean(integrations?.coderpad),
      CODILITY: Boolean(integrations?.codility),
      CODESIGNAL: Boolean(integrations?.codesignal),
      HACKERRANK: Boolean(integrations?.hackerrank),
    } satisfies Record<IntegrationSettingsPageKey, boolean>,
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- read by GraphQL code generation
const OrgSettingsNavIntegrations = gql`
  query OrgSettingsNavIntegrations {
    thisOrg {
      id
      integrations {
        id
        greenhouseOauth {
          subdomain
        }
        lever {
          active
        }
        ashby {
          apiKey
        }
        workday {
          tenant
        }
        microsoft {
          active
        }
        microsoftCustom {
          active
        }
        microsoftGccHigh {
          active
        }
        microsoftServiceUser {
          serviceAccountEmail
        }
        google {
          adminUserId
        }
        smartRecruiters {
          active
        }
        gem {
          apiKey
        }
        slack {
          active
        }
        zoom {
          active
        }
        zoomCustom {
          active
        }
        coderpad {
          apiKey
        }
        codility {
          apiKey
        }
        codesignal {
          apiKey
        }
        hackerrank {
          apiKey
        }
      }
    }
  }
`;
